import { css, styled } from "@mui/material";

import { CTAButton } from "@components/CTA/styles.styled";
import { getColor } from "@hooks";
import RawBracket from "@public/raw_bracket.svg";
import { cssForHeaderWithLinearGradient } from "@utils";

export const StyledContainer = styled("div")(
  ({ theme }) => css`
    ${theme.breakpoints.up("mobile")} {
      width: 100%;
      height: 226px;
    }

    ${theme.breakpoints.up("tablet")} {
      width: 676px;
      height: 320px;
      border-radius: 0 0 32px 32px;

      ${theme.palette.mode === "dark"
        ? css`
            background: linear-gradient(180deg, #0e121b 22%, #222530 100%);

            box-shadow:
              inset 0 -1px 1px 1px #e1e4ea26,
              0 2px 40px 10px #e1e4ea0c,
              0 0 16px -7px #e1e4ea0c;
          `
        : css`
            background: linear-gradient(180deg, #f2f5f8 22%, #f5f7fa 100%);

            box-shadow:
              inset 0 -1px 1px 1px #e1e4ea26,
              0 2px 40px 10px #7177840d,
              0 0 16px -7px #7177840d;

            border-width: 0px, 1px, 1px, 1px;
            border-style: solid;
            border-color: ${getColor(
              c => c.foundations.alpha.neutral["alpha-10"],
              theme,
            )};
          `}
    }

    ${theme.breakpoints.up("desktop")} {
      width: 914px;
      height: 432px;
    }

    ${theme.breakpoints.up("desktopXl")} {
      width: 1220px;
      height: 547px;
    }

    margin: 0 auto;
    overflow: hidden;
    position: relative;
  `,
);

export const EverybodyTextStyled = styled("div")(
  ({ theme }) => css`
    ${theme.breakpoints.up("mobile")} {
      font-size: 32px;
      line-height: 40px;
      font-weight: 500;
    }

    ${theme.breakpoints.up("tablet")} {
      font-size: 44px;
      line-height: 54px;
    }

    ${theme.breakpoints.up("desktop")} {
      font-size: 72px;
      line-height: 80px;
    }

    ${theme.breakpoints.up("desktopXl")} {
      font-size: 88px;
      line-height: 96px;
    }

    font-family: "Work Sans", sans-serif;
    font-weight: 600;
    line-height: 96px;
    color: ${getColor(c => c.tokens.text["strong-950"], theme)};
  `,
);

export const ITBuddyTextStyled = styled("div")(
  ({ theme }) => css`
    ${theme.breakpoints.up("mobile")} {
      font-size: 32px;
      line-height: 40px;
      font-weight: 500;
    }

    ${theme.breakpoints.up("tablet")} {
      font-size: 44px;
      line-height: 54px;
      font-weight: 400;
    }

    ${theme.breakpoints.up("desktop")} {
      font-size: 72px;
      line-height: 80px;
    }

    ${theme.breakpoints.up("desktopXl")} {
      font-size: 88px;
      line-height: 96px;
    }

    font-family: "Work Sans", sans-serif;

    ${theme.palette.mode === "dark"
      ? css`
          color: ${getColor(c => c.tokens.text["soft-400"], theme)};
        `
      : css`
          color: ${getColor(c => c.tokens.text["sub-600"], theme)};
        `}
  `,
);

export const ToScaleTextStyled = styled("div")(
  ({ theme }) => css`
    ${theme.breakpoints.up("mobile")} {
      font-size: 32px;
      line-height: 40px;
      min-width: 150px;
    }

    ${theme.breakpoints.up("tablet")} {
      font-size: 40px;
      line-height: 48px;
      min-width: 200px;
      padding-top: 1rem;
    }

    ${theme.breakpoints.up("desktop")} {
      font-size: 56px;
      line-height: 66px;
      min-width: 260px;
    }

    ${theme.breakpoints.up("desktopXl")} {
      font-size: 72px;
      line-height: 84px;
      min-width: 350px;
    }

    font-family: "Work Sans", sans-serif;
    font-weight: 500;
    font-size: 72px;
    line-height: 84px;

    ${cssForHeaderWithLinearGradient(
      theme,
      "linear-gradient(82.22deg, #05DF54 0%, #96C5F0 76%)",
    )}

    ${theme.palette.mode === "dark" &&
    css`
      text-shadow: 0 0 20px #33eb8373;
    `}
  `,
);

export const BracketStyled = styled(RawBracket)(
  ({ theme }) => css`
    ${theme.breakpoints.up("mobile")} {
      scale: 0.44;
      bottom: 91px;
      right: 0rem;
    }

    ${theme.breakpoints.up("tablet")} {
      scale: 0.53;
      bottom: 91px;
      right: 0.5rem;
    }

    ${theme.breakpoints.up("desktop")} {
      scale: 0.92;
      bottom: 149px;
      right: 3rem;
    }

    ${theme.breakpoints.up("desktopXl")} {
      scale: 1;
      bottom: 194px;
      right: 3rem;
    }

    filter: drop-shadow(0 0 20px #ffffff66);

    ${theme.palette.mode === "dark"
      ? css`
          color: ${getColor(c => c.tokens.text["strong-950"], theme)};
        `
      : css`
          color: ${getColor(c => c.tokens.text["sub-600"], theme)};
        `}

    position: absolute;
  `,
);

export const HeroTextContainer = styled("h1")(
  ({ theme }) => css`
    ${theme.breakpoints.up("mobile")} {
      bottom: 5.5rem;
      left: 1rem;
    }

    ${theme.breakpoints.up("tablet")} {
      bottom: 2rem;
      left: 2rem;
    }

    ${theme.breakpoints.up("desktop")} {
      bottom: 56px;
      left: 50px;
    }

    position: absolute;
  `,
);

export const ButtonStyled = styled(CTAButton)(
  ({ theme }) => css`
    ${theme.breakpoints.up("mobile")} {
      bottom: 0.5rem;
      right: 1.5rem;
    }

    ${theme.breakpoints.down("tablet")} {
      width: calc(100% - 3rem);
    }

    ${theme.breakpoints.up("tablet")} {
      bottom: 1.5rem;
      right: 1.5rem;
      padding: 0.5rem 0.75rem;
    }

    ${theme.breakpoints.up("desktop")} {
      bottom: 52px;
      right: 50px;
      padding: 0.875rem 1.125rem;
    }

    position: absolute;
    box-shadow: 0px 16px 100px 0px #0e121b0a;
  `,
);

export const AnimatedBackgroundMask = styled("div")`
  position: relative;
  mask-image: linear-gradient(transparent 0%, #f0f0f0 60%, black 100%);
  width: 100%;
  height: 100%;
`;

export const SrOnlyStyled = styled("span")(
  () => css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  `,
);
